import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "@/data/siteContent";
import Logo from "@/components/brand/Logo";
import ThemeToggle from "@/components/brand/ThemeToggle";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Areas", href: "#service-areas" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 dark:bg-[#0A2513]/85 backdrop-blur-xl border-b border-[#E3F5EA] dark:border-[#1A3925]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 h-[78px] flex items-center justify-between">
        <a
          href="#home"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <Logo size={44} data-testid="header-logo" />
          <span className="font-display text-lg font-semibold tracking-tight text-[#0E2A17] dark:text-[#F2FAF4]">
            Ecomold <span className="text-[#1F8A3A]">Clean</span>
          </span>
        </a>

        <nav
          className="hidden lg:flex items-center gap-8"
          data-testid="header-nav"
        >
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className="text-sm font-medium text-[#3F5549] hover:text-[#0E2A17] dark:text-[#F2FAF4] transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F8A3A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0F5C24] transition-colors"
          >
            <Phone className="h-4 w-4" />
            {COMPANY.phone}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <ThemeToggle />
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E3F5EA] dark:border-[#1A3925] text-[#0E2A17] dark:text-[#F2FAF4]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-[#E3F5EA] bg-white dark:bg-[#0A2513]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className="text-base font-medium text-[#0E2A17] dark:text-[#F2FAF4]"
              >
                {item.label}
              </a>
            ))}
            <a
              href={COMPANY.phoneHref}
              data-testid="mobile-header-phone-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1F8A3A] px-5 py-3 text-sm font-semibold text-white"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
